import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import * as quoteApi from './quoteApi';
import { LabourResult, Quote } from './quoteApi';

interface FinalViewProps {
  quote: Quote;
  onQuoteChange: (quote: Quote) => void;
  onCopied?: (quote: Quote) => void;
}

const FinalView: React.FC<FinalViewProps> = ({ quote, onQuoteChange, onCopied }) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [labour, setLabour] = useState<LabourResult | null>(null);
  const [audit, setAudit] = useState<Array<{ event_type: string; detail: string; created_by: string | null; created_at: string | null }>>([]);
  const [error, setError] = useState('');
  const finalised = quote.status === 'finalised';

  useEffect(() => {
    Promise.all([quoteApi.calculateLabour(quote.id), quoteApi.listAudit(quote.id)])
      .then(([calc, events]) => {
        setLabour(calc);
        setAudit(events);
      })
      .catch((err) => setError(err.message));
  }, [quote.id]);

  const handleFinalise = async () => {
    try {
      const updated = await quoteApi.finaliseQuote(quote.id);
      onQuoteChange(updated);
      setConfirmOpen(false);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  const rows = labour
    ? [
        ['Mechanical', labour.mechanical_hrs],
        ['Electrical', labour.electrical_hrs],
        ['Drilling', labour.drilling_hrs],
        ['Project', labour.project_hrs],
        ['Base', labour.base_hrs],
        ['Test', labour.test_hrs],
        ['Material management', labour.material_mgmt_hrs],
        ['H&B', labour.hb_hrs],
        ['After allowances', labour.after_allowances_hrs],
        ['Final', labour.final_hrs],
      ]
    : [];

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        {finalised ? (
          <Alert severity="success" sx={{ py: 0, flex: 1 }}>
            Finalised{quote.finalised_by ? ` by ${quote.finalised_by}` : ''} {quote.finalised_at ? `· ${new Date(quote.finalised_at).toLocaleString()}` : ''}. Labour export is enabled.
          </Alert>
        ) : (
          <Alert severity="warning" sx={{ py: 0, flex: 1 }}>
            Labour Excel unlocks after finalise. This lock cannot be undone; copy to revise.
          </Alert>
        )}
        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
          <Button size="small" variant="outlined" startIcon={<FileDownloadIcon />} href={quoteApi.exportUrl(quote.id, 'bom')} disabled={!quote.enhancement_complete}>
            BOM Excel
          </Button>
          <Button size="small" variant="outlined" startIcon={<FileDownloadIcon />} href={quoteApi.exportUrl(quote.id, 'labour')} disabled={!finalised}>
            Labour Excel
          </Button>
          {finalised && onCopied && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              onClick={async () => {
                try {
                  const copied = await quoteApi.copyQuote(quote.id);
                  onCopied(copied);
                } catch (err: any) {
                  setError(err.response?.data?.detail || err.message);
                }
              }}
            >
              Copy version
            </Button>
          )}
          {!finalised && (
            <Button size="small" color="error" variant="contained" onClick={() => setConfirmOpen(true)}>
              Mark as final
            </Button>
          )}
        </Box>
      </Box>

      {labour && <Chip size="small" label={`${labour.final_hrs.toFixed(2)} hrs factored`} color="primary" variant="outlined" sx={{ mb: 1 }} />}

      <TableContainer component={Paper} sx={{ mb: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Category</TableCell>
              <TableCell align="right">Hours</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(([label, value]) => (
              <TableRow key={String(label)}>
                <TableCell>{label}</TableCell>
                <TableCell align="right">{Number(value).toFixed(2)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="subtitle2" gutterBottom>Audit</Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>When</TableCell>
              <TableCell>Event</TableCell>
              <TableCell>Detail</TableCell>
              <TableCell>By</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {audit.map((event, index) => (
              <TableRow key={index}>
                <TableCell>{event.created_at ? new Date(event.created_at).toLocaleString() : '—'}</TableCell>
                <TableCell>{event.event_type}</TableCell>
                <TableCell>{event.detail}</TableCell>
                <TableCell>{event.created_by || '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Finalise this quote?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This locks {quote.quote_ref} v{quote.version}. Lines cannot be edited and the action cannot be undone. A new version can still be copied.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleFinalise}>Finalise</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FinalView;
